import { useEffect, useRef, useState } from "react";
import { LIGHTBULB_URL } from "@/constants/tutorial";
import type { FpEmailTipProps } from "@/interfaces/tutorial";
import { measureFpEmailTip } from "@/utils/tutorial-helper";
import { RightArrowNotch } from "./tip-arrows";

export default function FpEmailTip({ open, onGotIt }: FpEmailTipProps) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [top, setTop] = useState<number>(Math.floor(window.innerHeight * 0.38));
  const cardRef = useRef<HTMLDivElement>(null);
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    if (open) {
      setEntered(false);
      // Delay mount until the overlay's 1s fade-in completes — avoids appearing
      // before the background is visible (no CSS-delay workaround needed).
      unmountTimerRef.current = setTimeout(() => {
        setMounted(true);
        requestAnimationFrame(() => {
          if (cardRef.current) {
            const h = cardRef.current.getBoundingClientRect().height || 200;
            setTop(measureFpEmailTip(h));
          }
          requestAnimationFrame(() => setEntered(true));
        });
      }, 1000);
    } else {
      setEntered(false);
      unmountTimerRef.current = setTimeout(() => setMounted(false), 820);
    }
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
    };
  }, [open]);

  if (!mounted) return null;

  return (
    <div
      ref={cardRef}
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: 24,
        top,
        maxWidth: 360,
        background: "var(--color-white)",
        border: "1px solid var(--color-smoke)",
        borderRadius: 14,
        boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
        padding: "20px 22px",
        display: "flex",
        gap: 18,
        alignItems: "flex-start",
        fontFamily: "var(--font-lato)",
        fontSize: 15,
        lineHeight: 1.5,
        color: "var(--color-rodeo-black)",
        zIndex: 10001,
        transform: entered ? "translateX(0)" : "translateX(calc(-100% - 32px))",
        opacity: entered ? 1 : 0,
        transition: entered
          ? "transform 1.2s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1)"
          : "transform 0.8s cubic-bezier(0.5, 0, 0.75, 0), opacity 0.8s ease-in",
      }}
    >
      {/* Right-pointing arrow notch — aligns card center with the CTA button */}
      <RightArrowNotch />

      {/* Lightbulb illustration */}
      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 120,
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={LIGHTBULB_URL}
          alt=""
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          justifyContent: "center",
          minHeight: 120,
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--color-rodeo-black)",
            fontSize: 15,
            lineHeight: 1.5,
          }}
        >
          Click 'Choose a Gift' to preview your unboxing experience.
        </p>

        <button
          type="button"
          onClick={onGotIt}
          className="self-start inline-flex items-center bg-rodeo-black hover:bg-background-dark text-white border-none rounded-btn py-2 px-4 font-lato text-[11px] font-bold uppercase tracking-[1.5px] cursor-pointer transition-colors"
        >
          OK, got it
        </button>
      </div>
    </div>
  );
}
